import { createTheme } from '@mui/material/styles';

const commonComponents = {
  MuiButton: {
    styleOverrides: {
      root: {
        textTransform: 'none',
        borderRadius: '12px',
        fontWeight: 500
      }
    }
  },
  MuiTooltip: {
    styleOverrides: {
      tooltip: {
        borderRadius: 8,
        fontSize: '0.875rem'
      }
    }
  },
  MuiCard: {
    defaultProps: {
      elevation: 0
    }
  },
  MuiPaper: {
    defaultProps: {
      elevation: 0
    }
  }
};

export const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#2196F3',
      light: '#64B5F6',
      dark: '#1976D2',
      contrastText: '#fff'
    },
    secondary: {
      main: '#21CBF3',
      light: '#80DEEA',
      dark: '#0097A7',
      contrastText: '#fff'
    },
    background: {
      default: '#f5f7fa',
      paper: 'rgba(255, 255, 255, 0.9)'
    },
    text: {
      primary: 'rgba(0, 0, 0, 0.87)',
      secondary: 'rgba(0, 0, 0, 0.6)'
    }
  },
  components: {
    ...commonComponents,
    MuiCard: {
      ...commonComponents.MuiCard,
      styleOverrides: {
        root: {
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.3)',
          background: 'rgba(255, 255, 255, 0.9)',
          transition: 'all 0.3s ease-in-out',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 12px 24px rgba(0, 0, 0, 0.1)'
          }
        }
      }
    },
    MuiPaper: {
      ...commonComponents.MuiPaper,
      styleOverrides: {
        root: {
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.3)',
          background: 'rgba(255, 255, 255, 0.9)'
        }
      }
    }
  }
});

export const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#2196F3',
      light: '#64B5F6',
      dark: '#1976D2',
      contrastText: '#fff'
    },
    secondary: {
      main: '#21CBF3',
      light: '#80DEEA',
      dark: '#26C6DA',
      contrastText: '#fff'
    },
    background: {
      default: '#1E1E1E',
      paper: 'rgba(28, 28, 28, 0.95)'
    },
    text: {
      primary: '#fff',
      secondary: 'rgba(255, 255, 255, 0.7)'
    },
    divider: 'rgba(255, 255, 255, 0.1)'
  },
  components: {
    ...commonComponents,
    MuiCssBaseline: {
      styleOverrides: {
        '*, *::before, *::after': {
          boxSizing: 'border-box'
        },
        'html, body': {
          margin: 0,
          padding: 0,
          width: '100%',
          height: '100%'
        },
        body: {
          backgroundColor: '#1E1E1E !important'
        },
        '#root': {
          minHeight: '100vh',
          backgroundColor: '#1E1E1E !important'
        },
        '.MuiBox-root': {
          backgroundColor: '#1E1E1E !important'
        },
        '.css-1dv89sd': {
          backgroundColor: '#1E1E1E !important',
          background: 'none !important'
        }
      }
    },
    MuiBox: {
      defaultProps: {
        sx: {
          backgroundColor: '#1E1E1E !important'
        }
      },
      styleOverrides: {
        root: {
          backgroundColor: '#1E1E1E !important'
        }
      }
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#1E1E1E',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: 'none'
        }
      }
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          backgroundColor: '#1E1E1E !important'
        }
      }
    },
    MuiCard: {
      ...commonComponents.MuiCard,
      styleOverrides: {
        root: {
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          background: 'linear-gradient(to bottom right, rgba(28, 28, 28, 0.95), rgba(35, 35, 35, 0.95))',
          transition: 'all 0.3s ease-in-out',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 12px 24px rgba(0, 0, 0, 0.4)'
          }
        }
      }
    },
    MuiPaper: {
      ...commonComponents.MuiPaper,
      styleOverrides: {
        root: {
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          background: 'linear-gradient(to bottom right, rgba(28, 28, 28, 0.95), rgba(35, 35, 35, 0.95))'
        }
      }
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true
      },
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: '12px',
          fontWeight: 500,
          color: '#fff',
          transition: 'all 0.3s ease-in-out',
          background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
          border: 'none',
          '&:hover': {
            background: 'linear-gradient(45deg, #1976D2 30%, #0097A7 90%)',
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 16px rgba(33, 150, 243, 0.3)'
          },
          '&:disabled': {
            backgroundColor: 'rgba(26, 31, 44, 0.5)',
            color: 'rgba(255, 255, 255, 0.3)',
            background: 'none',
            border: '1px solid rgba(255, 255, 255, 0.05)'
          }
        },
        contained: {},
        outlined: {},
        text: {},
        sizeLarge: {
          padding: '12px 24px',
          fontSize: '1rem'
        }
      }
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: '#fff',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.1)'
          }
        }
      }
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          '&.MuiOutlinedInput-root': {
            backgroundColor: 'rgba(26, 31, 44, 0.95)',
            color: '#fff',
            '& fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.23)'
            },
            '&:hover fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.4)'
            },
            '&.Mui-focused fieldset': {
              borderColor: '#2196F3'
            }
          }
        }
      }
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: 'rgba(26, 31, 44, 0.95)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.1)'
        },
        list: {
          padding: 0
        }
      }
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            backgroundColor: 'rgba(26, 31, 44, 0.95)',
            backdropFilter: 'blur(20px)',
            '&:hover': {
              backgroundColor: 'rgba(31, 36, 49, 0.95)'
            }
          }
        },
        paper: {
          backgroundColor: 'rgba(26, 31, 44, 0.95) !important',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          marginTop: '4px',
          '& .MuiAutocomplete-option': {
            '&[aria-selected="true"]': {
              backgroundColor: 'rgba(33, 150, 243, 0.15)'
            },
            '&.Mui-focused': {
              backgroundColor: 'rgba(255, 255, 255, 0.05)'
            }
          }
        },
        listbox: {
          backgroundColor: 'transparent',
          padding: '4px',
          '& .MuiAutocomplete-option': {
            borderRadius: '8px',
            margin: '2px 0',
            '&:first-of-type': {
              marginTop: 0
            },
            '&:last-of-type': {
              marginBottom: 0
            }
          }
        },
        option: {
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.05)'
          }
        },
        input: {
          color: '#fff'
        },
        endAdornment: {
          color: 'rgba(255, 255, 255, 0.7)'
        }
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '&.MuiInputBase-root': {
            backgroundColor: 'rgba(26, 31, 44, 0.95)',
            color: '#fff',
            '& fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.23)'
            },
            '&:hover fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.4)'
            },
            '&.Mui-focused fieldset': {
              borderColor: '#2196F3'
            }
          }
        }
      }
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#1E1E1E',
          borderRight: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '4px 0 8px 0 rgba(0,0,0,0.2)'
        }
      }
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#1E1E1E',
          backgroundImage: 'none',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 2px 8px 0 rgba(0,0,0,0.2)'
        }
      }
    }
  }
});

export const createCustomTheme = (mode) => {
    return createTheme({
        palette: {
            mode,
            ...(mode === 'light'
                ? {
                    primary: {
                        main: '#2196F3',
                        light: '#64B5F6',
                        dark: '#1976D2',
                        contrastText: '#fff'
                    },
                    secondary: {
                        main: '#21CBF3',
                        light: '#80DEEA',
                        dark: '#0097A7',
                        contrastText: '#fff'
                    },
                    background: {
                        default: '#F8FAFC',
                        paper: '#FFFFFF'
                    },
                    text: {
                        primary: '#1A1F2C',
                        secondary: 'rgba(26, 31, 44, 0.7)'
                    }
                }
                : {
                    primary: {
                        main: '#90CAF9',
                        light: '#BBDEFB',
                        dark: '#42A5F5',
                        contrastText: '#1E1E1E'
                    },
                    secondary: {
                        main: '#21CBF3',
                        light: '#80DEEA',
                        dark: '#26C6DA',
                        contrastText: '#1E1E1E'
                    },
                    background: {
                        default: '#1E1E1E',
                        paper: '#232323'
                    },
                    text: {
                        primary: '#FFFFFF',
                        secondary: 'rgba(255, 255, 255, 0.7)'
                    }
                }),
        },
        typography: {
            fontFamily: [
                'Inter',
                '-apple-system',
                'BlinkMacSystemFont',
                '"Segoe UI"',
                'Roboto',
                '"Helvetica Neue"',
                'Arial',
                'sans-serif',
            ].join(','),
            h1: {
                fontSize: '2.5rem',
                fontWeight: 700,
                lineHeight: 1.2
            },
            h2: {
                fontSize: '2rem',
                fontWeight: 600,
                lineHeight: 1.3
            },
            h3: {
                fontSize: '1.75rem',
                fontWeight: 600,
                lineHeight: 1.3
            },
            h4: {
                fontSize: '1.5rem',
                fontWeight: 600,
                lineHeight: 1.4
            },
            h5: {
                fontSize: '1.25rem',
                fontWeight: 600,
                lineHeight: 1.4
            },
            h6: {
                fontSize: '1rem',
                fontWeight: 600,
                lineHeight: 1.5
            },
            body1: {
                fontSize: '1rem',
                lineHeight: 1.5
            },
            body2: {
                fontSize: '0.875rem',
                lineHeight: 1.57
            },
            button: {
                textTransform: 'none',
                fontWeight: 600
            }
        },
        shape: {
            borderRadius: 12
        },
        components: {
            MuiCssBaseline: {
                styleOverrides: {
                    '*': {
                        boxSizing: 'border-box',
                        margin: 0,
                        padding: 0
                    },
                    html: {
                        MozOsxFontSmoothing: 'grayscale',
                        WebkitFontSmoothing: 'antialiased',
                        display: 'flex',
                        flexDirection: 'column',
                        minHeight: '100%',
                        width: '100%'
                    },
                    body: {
                        display: 'flex',
                        flex: '1 1 auto',
                        flexDirection: 'column',
                        minHeight: '100%',
                        width: '100%'
                    },
                    '#root': {
                        display: 'flex',
                        flex: '1 1 auto',
                        flexDirection: 'column',
                        height: '100%',
                        width: '100%'
                    }
                }
            },
            MuiButton: {
                styleOverrides: {
                    root: {
                        borderRadius: 8,
                        textTransform: 'none',
                        fontWeight: 600,
                        padding: '8px 16px',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                            transform: 'translateY(-1px)'
                        }
                    },
                    contained: {
                        boxShadow: 'none',
                        '&:hover': {
                            boxShadow: mode === 'dark'
                                ? '0 4px 12px rgba(0,0,0,0.5)'
                                : '0 4px 12px rgba(0,0,0,0.1)'
                        }
                    },
                    outlined: {
                        borderWidth: '1.5px',
                        '&:hover': {
                            borderWidth: '1.5px'
                        }
                    }
                },
                defaultProps: {
                    disableElevation: true
                }
            },
            MuiCard: {
                styleOverrides: {
                    root: {
                        borderRadius: 12,
                        boxShadow: mode === 'dark'
                            ? '0 2px 8px 0 rgba(0,0,0,0.3)'
                            : '0 2px 8px 0 rgba(0,0,0,0.05)',
                        backgroundImage: 'none'
                    }
                }
            },
            MuiPaper: {
                styleOverrides: {
                    root: {
                        backgroundImage: 'none'
                    }
                }
            },
            MuiDrawer: {
                styleOverrides: {
                    paper: {
                        backgroundColor: mode === 'dark' ? '#1E1E1E' : '#FFFFFF',
                        borderRight: mode === 'dark' 
                            ? '1px solid rgba(255, 255, 255, 0.1)'
                            : '1px solid rgba(0, 0, 0, 0.1)',
                        boxShadow: mode === 'dark'
                            ? '4px 0 8px 0 rgba(0,0,0,0.2)'
                            : '4px 0 8px 0 rgba(0,0,0,0.05)'
                    }
                }
            },
            MuiAppBar: {
                styleOverrides: {
                    root: {
                        backgroundColor: mode === 'dark' ? '#1E1E1E' : '#FFFFFF',
                        backgroundImage: 'none',
                        borderBottom: mode === 'dark'
                            ? '1px solid rgba(255, 255, 255, 0.1)'
                            : '1px solid rgba(0, 0, 0, 0.1)',
                        boxShadow: mode === 'dark'
                            ? '0 2px 8px 0 rgba(0,0,0,0.2)'
                            : '0 2px 8px 0 rgba(0,0,0,0.05)'
                    }
                },
                defaultProps: {
                    color: 'default'
                }
            },
            MuiListItemButton: {
                styleOverrides: {
                    root: {
                        borderRadius: 8,
                        margin: '4px 8px',
                        padding: '8px 12px',
                        '&.Mui-selected': {
                            backgroundColor: mode === 'dark'
                                ? 'rgba(144, 202, 249, 0.16)'
                                : 'rgba(33, 150, 243, 0.08)',
                            '&:hover': {
                                backgroundColor: mode === 'dark'
                                    ? 'rgba(144, 202, 249, 0.24)'
                                    : 'rgba(33, 150, 243, 0.12)'
                            }
                        }
                    }
                }
            },
            MuiAutocomplete: {
                styleOverrides: {
                    root: {
                        '& .MuiOutlinedInput-root': {
                            backgroundColor: mode === 'dark'
                                ? '#1E1E1E'
                                : 'rgba(255, 255, 255, 0.9)',
                            backdropFilter: 'blur(20px)',
                            '&:hover': {
                                backgroundColor: mode === 'dark'
                                    ? '#232323'
                                    : 'rgba(255, 255, 255, 1)'
                            }
                        }
                    },
                    paper: {
                        backgroundColor: mode === 'dark'
                            ? '#1E1E1E !important'
                            : 'rgba(255, 255, 255, 0.9) !important',
                        backdropFilter: 'blur(20px)',
                        border: '1px solid',
                        borderColor: mode === 'dark'
                            ? 'rgba(255, 255, 255, 0.1)'
                            : 'rgba(0, 0, 0, 0.1)',
                        marginTop: '4px',
                        '& .MuiAutocomplete-option': {
                            '&[aria-selected="true"]': {
                                backgroundColor: mode === 'dark'
                                    ? 'rgba(255, 255, 255, 0.08)'
                                    : 'rgba(33, 150, 243, 0.15)'
                            },
                            '&.Mui-focused': {
                                backgroundColor: mode === 'dark'
                                    ? 'rgba(255, 255, 255, 0.05)'
                                    : 'rgba(0, 0, 0, 0.02)'
                            }
                        }
                    },
                    listbox: {
                        backgroundColor: 'transparent',
                        padding: '4px',
                        '& .MuiAutocomplete-option': {
                            borderRadius: '8px',
                            margin: '2px 0',
                            '&:first-of-type': {
                                marginTop: 0
                            },
                            '&:last-of-type': {
                                marginBottom: 0
                            }
                        }
                    },
                    option: {
                        '&:hover': {
                            backgroundColor: mode === 'dark'
                                ? 'rgba(255, 255, 255, 0.05)'
                                : 'rgba(0, 0, 0, 0.02)'
                        }
                    },
                    input: {
                        color: mode === 'dark'
                            ? '#fff'
                            : '#1a1f2c'
                    },
                    endAdornment: {
                        color: mode === 'dark'
                            ? 'rgba(255, 255, 255, 0.7)'
                            : 'rgba(0, 0, 0, 0.7)'
                    }
                }
            }
        }
    });
};
 